/**
 * Admin sub-page — sends + tracks sign-up invitations for CHU partners.
 *
 * Mounted as a tab inside /admin (see UserModuleAdmin.tsx). Each invite
 * pre-assigns an Organization + a plan tier; the recipient receives an
 * email with a single-use link that lands on /register with the email
 * locked and the org/plan baked in server-side.
 */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { formatDistanceToNow } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuBuilding2,
  LuCheck,
  LuCalendarPlus,
  LuClock,
  LuInfinity,
  LuInfo,
  LuMail,
  LuRefreshCw,
  LuSend,
  LuShieldX,
  LuTrash2,
  LuTimer,
  LuTriangleAlert,
  LuUserPlus,
} from 'react-icons/lu'
import {
  Invitation,
  InvitationPlanTier,
  InvitationStatus,
  useCreateInvitation,
  useInvitations,
  useResendInvitation,
  useRevokeInvitation,
  usePurgeInvitation,
  useUpdateUserAccessExpiry,
} from '../../api/api_hooks/admin/invitations'
import OrganizationSearch from '../../components/Forms/components/OrganizationSearch'
import { INVITABLE_PLANS, PLAN_CONFIG, planColor, planLabel } from '../../config/plans'
import { CHROME_HEADER_GRADIENT, CHROME_GLASS_BORDER } from '../../theme/chrome'

// Real user-facing labels — see src/config/plans.ts for the single source of truth.
const PLAN_OPTIONS: { value: InvitationPlanTier; label: string }[] = INVITABLE_PLANS.map((p) => ({
  value: p as InvitationPlanTier,
  label: PLAN_CONFIG[p].label,
}))

const STATUS_TONE: Record<InvitationStatus, { scheme: string; icon: any; label: string }> = {
  pending: { scheme: 'orange', icon: LuClock, label: 'En attente' },
  accepted: { scheme: 'green', icon: LuCheck, label: 'Acceptée' },
  expired: { scheme: 'gray', icon: LuTriangleAlert, label: 'Expirée' },
  revoked: { scheme: 'red', icon: LuShieldX, label: 'Révoquée' },
}

const STATUS_FILTERS: InvitationStatus[] = ['pending', 'accepted', 'expired', 'revoked']

// Same "grow the limit" pattern as PilotDetailPanel's "Charger plus de cas"
// and CaseVisitorsAdmin — the backend already paginates for real
// (admin-invitations.js: page/limit, default 50), this just wasn't wired
// up on this page before.
const INVITATIONS_PAGE_SIZE = 50

export default function InvitationsAdmin() {
  const { i18n } = useTranslation()
  const locale = i18n.language?.startsWith('fr') ? fr : enUS
  const toast = useToast()

  const [statusFilter, setStatusFilter] = useState<InvitationStatus | undefined>(undefined)
  const [search, setSearch] = useState('')
  const [limit, setLimit] = useState(INVITATIONS_PAGE_SIZE)
  const { data, isLoading, isFetching } = useInvitations({ status: statusFilter, search: search || undefined, limit })

  const counts = useMemo(() => {
    const c: Record<InvitationStatus, number> = { pending: 0, accepted: 0, expired: 0, revoked: 0 }
    data?.data.forEach((inv) => { c[inv.status]++ })
    return c
  }, [data])

  // ── Create modal state ──
  const create = useDisclosure()
  const createMutation = useCreateInvitation()
  const [form, setForm] = useState({
    email: '',
    organizationId: '' as string | '',
    organizationName: '' as string,
    planTier: 'enterprise' as InvitationPlanTier,
    message: '',
    ttlDays: 14,
    accessDays: 90,
  })
  const resetForm = () => setForm({ email: '', organizationId: '', organizationName: '', planTier: 'enterprise', message: '', ttlDays: 14, accessDays: 90 })

  const submitCreate = async () => {
    if (!form.email.trim()) {
      toast({ title: 'Email requis', status: 'warning', duration: 2500 })
      return
    }
    if (!form.organizationId && !form.organizationName.trim()) {
      toast({ title: 'Organisation requise', description: 'Choisis-en une existante ou crée-la à la volée.', status: 'warning', duration: 3500 })
      return
    }

    try {
      await createMutation.mutateAsync({
        email: form.email.trim().toLowerCase(),
        organizationId: form.organizationId || undefined,
        organizationName: form.organizationId ? undefined : form.organizationName.trim(),
        planTier: form.planTier,
        message: form.message.trim() || undefined,
        ttlDays: form.ttlDays || undefined,
        accessDays: form.accessDays || undefined,
      })
      toast({ title: 'Invitation envoyée', description: form.email, status: 'success', duration: 3000 })
      resetForm()
      create.onClose()
    } catch (e: any) {
      const status = e?.response?.status
      const key = e?.response?.data?.key as string | undefined

      // 409 duplicate-email cases — give the admin a clear next step:
      // re-send the existing invitation, scroll to it in the table, or
      // (for accepted) explain there's already a user account.
      if (status === 409 && key === 'error.invitation.already-pending') {
        toast({
          title: 'Invitation déjà en cours',
          description: `Un email a déjà été envoyé à ${form.email}. Utilisez "Renvoyer" sur la ligne existante plutôt que d'en créer une nouvelle.`,
          status: 'warning',
          duration: 7000,
          isClosable: true,
        })
        create.onClose()
        setStatusFilter('pending')
        setSearch(form.email.trim().toLowerCase())
        return
      }
      if (status === 409 && key === 'error.invitation.already-accepted') {
        toast({
          title: 'Compte déjà créé',
          description: `${form.email} a déjà accepté une invitation. Le compte existe — pas besoin d'inviter à nouveau.`,
          status: 'warning',
          duration: 7000,
          isClosable: true,
        })
        create.onClose()
        setStatusFilter('accepted')
        setSearch(form.email.trim().toLowerCase())
        return
      }
      if (status === 409 && key === 'error.email-already-use') {
        toast({
          title: 'Utilisateur déjà inscrit',
          description: `${form.email} est déjà un compte Careflai vérifié. Ils n'ont pas besoin d'invitation.`,
          status: 'warning',
          duration: 7000,
          isClosable: true,
        })
        return
      }

      const msg = e?.response?.data?.message || e?.message || 'Erreur inconnue'
      toast({ title: 'Envoi impossible', description: msg, status: 'error', duration: 5000 })
    }
  }

  // ── Resend + Revoke + Extend access ──
  const resend = useResendInvitation()
  const revoke = useRevokeInvitation()
  const extendAccess = useUpdateUserAccessExpiry()

  const handleExtendAccess = async (inv: Invitation, days: number | null) => {
    if (!inv.acceptedBy?.id) return
    try {
      const next = days === null
        ? null
        : (() => {
            // Extend from the current expiry if still in the future, else from now.
            const current = inv.acceptedBy?.accessExpiresAt
            const base = current && new Date(current) > new Date() ? new Date(current) : new Date()
            return new Date(base.getTime() + days * 24 * 60 * 60 * 1000).toISOString()
          })()
      await extendAccess.mutateAsync({ userId: inv.acceptedBy.id, accessExpiresAt: next })
      toast({
        title: days === null ? 'Accès illimité accordé' : `+${days} jours d'accès`,
        description: inv.acceptedBy.email,
        status: 'success',
        duration: 2500,
      })
    } catch (e: any) {
      toast({ title: 'Erreur', description: e?.response?.data?.message || e?.message, status: 'error' })
    }
  }
  const revokeDialog = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [pendingRevoke, setPendingRevoke] = useState<Invitation | null>(null)

  const handleResend = async (inv: Invitation) => {
    try {
      await resend.mutateAsync({ id: inv.id })
      toast({ title: 'Email renvoyé', description: inv.email, status: 'success', duration: 2500 })
    } catch (e: any) {
      toast({ title: 'Erreur', description: e?.response?.data?.message || e?.message, status: 'error' })
    }
  }

  const purge = usePurgeInvitation()
  const purgeDialog = useDisclosure()
  const [pendingPurge, setPendingPurge] = useState<Invitation | null>(null)

  const confirmPurge = async () => {
    if (!pendingPurge) return
    try {
      await purge.mutateAsync(pendingPurge.id)
      toast({ title: 'Invitation supprimée', description: pendingPurge.email, status: 'success', duration: 2500 })
    } catch (e: any) {
      toast({ title: 'Erreur', description: e?.response?.data?.message || e?.message, status: 'error' })
    } finally {
      setPendingPurge(null)
      purgeDialog.onClose()
    }
  }

  const confirmRevoke = async () => {
    if (!pendingRevoke) return
    try {
      await revoke.mutateAsync(pendingRevoke.id)
      toast({ title: 'Invitation révoquée', status: 'success', duration: 2500 })
    } catch (e: any) {
      toast({ title: 'Erreur', description: e?.response?.data?.message || e?.message, status: 'error' })
    } finally {
      setPendingRevoke(null)
      revokeDialog.onClose()
    }
  }

  return (
    <VStack spacing={5} align="stretch">
      {/* ── Header ── */}
      <Card borderRadius="xl" borderWidth="1px" borderColor="gray.100" overflow="hidden">
        <Box bgGradient={CHROME_HEADER_GRADIENT} p={5} position="relative" overflow="hidden" sx={{ '&::after': CHROME_GLASS_BORDER }}>
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Box w="44px" h="44px" borderRadius="xl" bg="whiteAlpha.300" display="grid" sx={{ placeItems: 'center' }} backdropFilter="blur(8px)">
                <Icon as={LuUserPlus} boxSize={5} color="white" />
              </Box>
              <VStack align="start" spacing={0}>
                <Heading size="md" color="white">Accès CHU</Heading>
                <Text fontSize="xs" color="whiteAlpha.900">
                  Invitez un partenaire à créer son compte avec organisation &amp; formule pré-assignées
                </Text>
              </VStack>
            </HStack>
            <Button
              leftIcon={<LuSend />}
              onClick={create.onOpen}
              bg="white"
              color="purple.700"
              _hover={{ bg: 'whiteAlpha.900', transform: 'translateY(-1px)' }}
              borderRadius="xl"
              fontWeight="700"
            >
              Inviter
            </Button>
          </HStack>
        </Box>

        {/* Status pills */}
        <CardBody py={4}>
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3}>
            <StatPill label="Toutes" value={data?.meta.totalItems ?? 0} active={!statusFilter} onClick={() => { setStatusFilter(undefined); setLimit(INVITATIONS_PAGE_SIZE) }} />
            {STATUS_FILTERS.map((s) => (
              <StatPill
                key={s}
                label={STATUS_TONE[s].label}
                value={counts[s]}
                color={STATUS_TONE[s].scheme}
                icon={STATUS_TONE[s].icon}
                active={statusFilter === s}
                onClick={() => { setStatusFilter(statusFilter === s ? undefined : s); setLimit(INVITATIONS_PAGE_SIZE) }}
              />
            ))}
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* ── Search ── */}
      <HStack>
        <Input
          placeholder="Rechercher par email…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setLimit(INVITATIONS_PAGE_SIZE) }}
          bg="white"
          borderRadius="xl"
        />
      </HStack>

      {/* ── Table ── */}
      <Card borderRadius="xl" borderWidth="1px" borderColor="gray.200">
        <CardBody p={0}>
          {isLoading ? (
            <HStack p={8} justify="center"><Spinner /><Text color="gray.600">Chargement…</Text></HStack>
          ) : !data?.data.length ? (
            <VStack p={10} spacing={3}>
              <Icon as={LuMail} boxSize={8} color="gray.300" />
              <Text color="gray.600" fontWeight="600">Aucune invitation</Text>
              <Text fontSize="sm" color="gray.500">Cliquez sur « Inviter » pour envoyer votre première invitation.</Text>
            </VStack>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Destinataire</Th>
                    <Th>Organisation</Th>
                    <Th>Plan</Th>
                    <Th>Statut</Th>
                    {/* "Accès plateforme" was read as a credit allowance. Name the
                        unit (durée) and spell out the distinction in a tooltip. */}
                    <Th>
                      <Tooltip
                        label="Jusqu'à quand le compte reste accessible (accessExpiresAt). Sans rapport avec les crédits d'analyse, qui se comptent à part."
                        hasArrow
                        placement="top"
                        openDelay={200}
                      >
                        <HStack spacing={1} display="inline-flex" cursor="help">
                          <Text as="span">Durée d'accès</Text>
                          <Icon as={LuInfo} boxSize={3} color="gray.400" />
                        </HStack>
                      </Tooltip>
                    </Th>
                    <Th>Envoyé par</Th>
                    <Th>Envoyé / Expire</Th>
                    <Th textAlign="right">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.data.map((inv) => {
                    const tone = STATUS_TONE[inv.status]
                    const expired = inv.status === 'pending' && new Date(inv.expiresAt) < new Date()
                    return (
                      <Tr key={inv.id} _hover={{ bg: 'gray.50' }}>
                        <Td>
                          <Text fontWeight="600" color="gray.800" fontSize="sm">{inv.email}</Text>
                          {inv.message && (
                            <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={1} maxW="280px">
                              « {inv.message} »
                            </Text>
                          )}
                        </Td>
                        <Td>
                          {inv.organization ? (
                            <HStack spacing={2}>
                              <Icon as={LuBuilding2} color="purple.500" boxSize={4} />
                              <VStack align="start" spacing={0}>
                                <Text fontSize="sm" color="gray.800">{inv.organization.officialName}</Text>
                                <Badge colorScheme="purple" variant="subtle" fontSize="2xs">{inv.organization.type.toUpperCase()}</Badge>
                              </VStack>
                            </HStack>
                          ) : <Text fontSize="sm" color="gray.400">—</Text>}
                        </Td>
                        <Td>
                          <Badge colorScheme={planColor(inv.planTier)} variant="solid">
                            {planLabel(inv.planTier)}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={tone.scheme} variant="subtle">
                            <HStack spacing={1}>
                              <Icon as={tone.icon} boxSize={3} />
                              <Text>{tone.label}</Text>
                            </HStack>
                          </Badge>
                        </Td>
                        <Td>
                          {renderAccessBadge(inv)}
                        </Td>
                        <Td>
                          <Text fontSize="xs" color="gray.700">{inv.sentBy?.contactName || inv.sentBy?.email || '—'}</Text>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="xs" color="gray.600">
                              Il y a {formatDistanceToNow(new Date(inv.sentAt), { locale })}
                            </Text>
                            <Text fontSize="2xs" color={expired ? 'red.500' : 'gray.500'}>
                              {inv.status === 'pending'
                                ? (expired ? 'Expirée' : `Expire ${formatDistanceToNow(new Date(inv.expiresAt), { addSuffix: true, locale })}`)
                                : (inv.acceptedAt ? `Acceptée ${formatDistanceToNow(new Date(inv.acceptedAt), { addSuffix: true, locale })}` : '')}
                            </Text>
                          </VStack>
                        </Td>
                        <Td textAlign="right">
                          <HStack spacing={1} justify="flex-end">
                            {inv.status === 'accepted' && inv.acceptedBy?.id && (
                              <>
                                <ExtendAccessButton
                                  isPending={extendAccess.isPending}
                                  onExtend={(days) => handleExtendAccess(inv, days)}
                                />
                                <Tooltip label="Accès illimité (passe le pilote en compte standard)" hasArrow>
                                  <Button
                                    size="xs"
                                    leftIcon={<LuInfinity />}
                                    variant="ghost"
                                    colorScheme="gray"
                                    isDisabled={extendAccess.isPending || inv.acceptedBy.accessExpiresAt === null}
                                    onClick={() => handleExtendAccess(inv, null)}
                                  >
                                    Illimité
                                  </Button>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip label={inv.status === 'accepted' ? 'Déjà acceptée' : 'Renvoyer l’email'} hasArrow>
                              <Button
                                size="xs"
                                leftIcon={<LuRefreshCw />}
                                variant="ghost"
                                colorScheme="teal"
                                isDisabled={inv.status === 'accepted' || inv.status === 'revoked' || resend.isPending}
                                onClick={() => handleResend(inv)}
                              >
                                Renvoyer
                              </Button>
                            </Tooltip>
                            <Tooltip label={inv.status === 'accepted' ? 'Compte déjà créé' : 'Révoquer'} hasArrow>
                              <Button
                                size="xs"
                                leftIcon={<LuShieldX />}
                                variant="ghost"
                                colorScheme="red"
                                isDisabled={inv.status === 'accepted' || inv.status === 'revoked'}
                                onClick={() => { setPendingRevoke(inv); revokeDialog.onOpen() }}
                              >
                                Révoquer
                              </Button>
                            </Tooltip>
                            {/* Suppression définitive — pour les adresses de
                                test jetables, dont la trace d'audit n'a aucune
                                valeur. Refusée tant que le compte accepté
                                existe ; une fois celui-ci supprimé, la ligne ne
                                conserve plus qu'un email et devient
                                supprimable (le FK est onDelete: SET NULL). */}
                            <Tooltip
                              label={inv.status === 'accepted' && inv.acceptedBy?.id
                                ? 'Compte existant — supprimer le compte à la place'
                                : 'Supprimer définitivement'}
                              hasArrow
                            >
                              <IconButton
                                aria-label="Supprimer définitivement"
                                size="xs"
                                icon={<LuTrash2 />}
                                variant="ghost"
                                colorScheme="red"
                                isDisabled={(inv.status === 'accepted' && !!inv.acceptedBy?.id) || purge.isPending}
                                onClick={() => { setPendingPurge(inv); purgeDialog.onOpen() }}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
              {isFetching && (
                <HStack px={4} py={2} bg="gray.50" justify="center">
                  <Spinner size="xs" /><Text fontSize="xs" color="gray.500">Mise à jour…</Text>
                </HStack>
              )}
              {!isFetching && data && data.data.length < data.meta.totalItems && (
                <HStack px={4} py={3} justify="center" borderTop="1px solid" borderColor="gray.100">
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="teal"
                    onClick={() => setLimit((n) => n + INVITATIONS_PAGE_SIZE)}
                  >
                    Charger plus d'invitations ({data.data.length} / {data.meta.totalItems})
                  </Button>
                </HStack>
              )}
            </Box>
          )}
        </CardBody>
      </Card>

      {/* ── Create modal ── */}
      <Modal isOpen={create.isOpen} onClose={() => { create.onClose(); resetForm() }} size="lg" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" overflow="hidden">
          <ModalHeader bgGradient={CHROME_HEADER_GRADIENT} color="white">
            <HStack spacing={3}>
              <Icon as={LuSend} />
              <Text>Nouvelle invitation</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={5}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Adresse email du destinataire</FormLabel>
                <Input
                  type="email"
                  placeholder="dr.dupont@chu-nancy.fr"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  bg="white"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Organisation</FormLabel>
                <OrganizationSearch
                  label=""
                  placeholder="CHU, hôpital, laboratoire..."
                  onOrganizationSelect={(orgId, companyName) => {
                    setForm((f) => ({
                      ...f,
                      organizationId: orgId || '',
                      organizationName: orgId ? '' : companyName,
                    }))
                  }}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Choisissez une organisation existante, ou tapez un nouveau nom (créée à la volée, type CHU par défaut).
                </Text>
              </FormControl>

              <SimpleGrid columns={2} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Plan</FormLabel>
                  <Select
                    value={form.planTier}
                    onChange={(e) => setForm((f) => ({ ...f, planTier: e.target.value as InvitationPlanTier }))}
                    bg="white"
                  >
                    {PLAN_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </Select>
                </FormControl>
                <FormControl>
                  {/* Renamed: sits next to "Durée d'accès" and both were called
                      "expire", which made the link TTL look like the pilot window. */}
                  <FormLabel fontSize="sm">Lien valable (jours)</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    max={90}
                    value={form.ttlDays}
                    onChange={(e) => setForm((f) => ({ ...f, ttlDays: Number(e.target.value) || 14 }))}
                    bg="white"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontSize="sm">
                  <HStack spacing={1.5}>
                    <Text as="span">Durée d'accès accordée</Text>
                    <Tooltip
                      label="Combien de temps le compte restera accessible après activation. Le décompte démarre à l'activation, pas à l'envoi. Modifiable ensuite depuis la colonne « Durée d'accès »."
                      hasArrow
                      placement="top"
                    >
                      <Box as="span" display="inline-flex" cursor="help">
                        <Icon as={LuInfo} boxSize={3} color="gray.400" />
                      </Box>
                    </Tooltip>
                  </HStack>
                </FormLabel>
                <Select
                  value={form.accessDays}
                  onChange={(e) => setForm((f) => ({ ...f, accessDays: Number(e.target.value) }))}
                  bg="white"
                >
                  <option value={30}>30 jours (1 mois)</option>
                  <option value={60}>60 jours (2 mois)</option>
                  <option value={90}>90 jours (3 mois) — par défaut</option>
                  <option value={180}>180 jours (6 mois)</option>
                  <option value={365}>365 jours (1 an)</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Message personnel (optionnel)</FormLabel>
                <Textarea
                  placeholder="Un mot pour le destinataire — apparaît dans le mail."
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  rows={3}
                  bg="white"
                  maxLength={1000}
                />
              </FormControl>

              <Divider />

              <Box bg="purple.50" border="1px solid" borderColor="purple.100" borderRadius="lg" p={3}>
                <HStack align="start" spacing={2}>
                  <Icon as={LuMail} color="purple.600" mt={0.5} />
                  <VStack align="start" spacing={0.5}>
                    <Text fontSize="sm" fontWeight="600" color="purple.800">
                      Ce qui sera envoyé
                    </Text>
                    <Text fontSize="xs" color="purple.700">
                      Un email avec un lien unique vers <code>/register?invitationToken=…</code>.
                      L'email du destinataire et son organisation sont verrouillés côté serveur — il
                      n'a qu'à choisir un mot de passe.
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.100">
            <Button variant="ghost" mr={2} onClick={() => { create.onClose(); resetForm() }}>
              Annuler
            </Button>
            <Button
              colorScheme="purple"
              leftIcon={<LuSend />}
              onClick={submitCreate}
              isLoading={createMutation.isPending}
              loadingText="Envoi…"
            >
              Envoyer l'invitation
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Revoke confirm ── */}
      <AlertDialog isOpen={revokeDialog.isOpen} leastDestructiveRef={cancelRef} onClose={revokeDialog.onClose} isCentered>
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="xl">
          <AlertDialogHeader>Révoquer l'invitation</AlertDialogHeader>
          <AlertDialogBody>
            Le lien envoyé à <Text as="span" fontWeight="700">{pendingRevoke?.email}</Text> sera immédiatement invalidé.
            L'invitation reste en historique pour audit.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={revokeDialog.onClose}>Annuler</Button>
            <Button colorScheme="red" ml={3} onClick={confirmRevoke} isLoading={revoke.isPending}>
              Révoquer
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog isOpen={purgeDialog.isOpen} leastDestructiveRef={cancelRef} onClose={purgeDialog.onClose} isCentered>
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="xl">
          <AlertDialogHeader>Supprimer définitivement</AlertDialogHeader>
          <AlertDialogBody>
            <Text>
              L'invitation de <Text as="span" fontWeight="700">{pendingPurge?.email}</Text> sera
              retirée de la base, sans trace en historique. Les partages de collaborateur créés
              pour cette adresse seront supprimés avec elle.
            </Text>
            <Text mt={3} fontSize="sm" color="gray.600">
              Cette action est irréversible. Elle est faite pour les adresses de test ; pour une
              invitation réelle, « Révoquer » conserve la trace d'audit.
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={purgeDialog.onClose}>Annuler</Button>
            <Button colorScheme="red" ml={3} onClick={confirmPurge} isLoading={purge.isPending}>
              Supprimer
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </VStack>
  )
}

// ── Stat pill (used in the header) ──────────────────────────────────────

/**
 * "Prolonger l'accès" popover — quick presets (30/60/90j) + custom amount,
 * same pattern as PilotsAdmin's credits-grant popover. Extending from the
 * CURRENT expiry (if still future) or from now (if already expired) is
 * handled by the caller (handleExtendAccess) — this component only picks
 * the number of days to add.
 */
function ExtendAccessButton({
  onExtend,
  isPending,
}: {
  onExtend: (days: number) => void
  isPending: boolean
}) {
  const [amount, setAmount] = useState('')

  const extend = (days: number, onClose: () => void) => {
    if (!Number.isFinite(days) || days <= 0) return
    onExtend(Math.round(days))
    onClose()
  }

  return (
    <Popover placement="left" isLazy>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Button
              size="xs"
              leftIcon={<LuCalendarPlus />}
              variant="ghost"
              colorScheme="purple"
              isDisabled={isPending}
              title="Prolonger l'accès"
            >
              Prolonger
            </Button>
          </PopoverTrigger>
          <PopoverContent w="220px">
            <PopoverArrow />
            <PopoverBody>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Prolonger l'accès de…
              </Text>
              <HStack spacing={1} mb={2}>
                {[30, 60, 90].map((d) => (
                  <Button
                    key={d}
                    size="xs"
                    variant="outline"
                    colorScheme="purple"
                    flex={1}
                    isLoading={isPending}
                    onClick={() => extend(d, onClose)}
                  >
                    +{d}j
                  </Button>
                ))}
              </HStack>
              <HStack>
                <NumberInput size="sm" value={amount} min={1} onChange={(s) => setAmount(s)}>
                  <NumberInputField placeholder="Jours" />
                </NumberInput>
                <IconButton
                  aria-label="Prolonger"
                  icon={<LuCheck />}
                  size="sm"
                  colorScheme="purple"
                  isLoading={isPending}
                  onClick={() => extend(Number(amount), onClose)}
                />
              </HStack>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  )
}

/**
 * Renders the "Accès plateforme" cell for an invitation row.
 * Shape changes with the user's accessExpiresAt:
 *   - never accepted     → "—"
 *   - null               → "Illimité" (gray pill)
 *   - past               → "Expiré" (red pill)
 *   - 0-7 days remaining → red pill "X j restants"
 *   - 8-30 days          → orange pill
 *   - >30 days           → teal pill
 */
function renderAccessBadge(inv: Invitation) {
  if (inv.status !== 'accepted' || !inv.acceptedBy) {
    return <Text fontSize="xs" color="gray.400">—</Text>
  }
  const exp = inv.acceptedBy.accessExpiresAt
  if (!exp) {
    return (
      <Badge colorScheme="gray" variant="subtle" display="inline-flex" alignItems="center" gap={1}>
        <Icon as={LuInfinity} boxSize={3} />
        <Text>Illimité</Text>
      </Badge>
    )
  }
  const now = new Date()
  const expDate = new Date(exp)
  const msLeft = expDate.getTime() - now.getTime()
  const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000))
  if (daysLeft <= 0) {
    return (
      <Badge colorScheme="red" variant="solid" display="inline-flex" alignItems="center" gap={1}>
        <Icon as={LuTriangleAlert} boxSize={3} />
        <Text>Expiré</Text>
      </Badge>
    )
  }
  const tone = daysLeft <= 7 ? 'red' : daysLeft <= 30 ? 'orange' : 'teal'
  const label = daysLeft <= 1 ? "1 jour restant" : `${daysLeft} jours restants`
  return (
    <Badge colorScheme={tone} variant="subtle" display="inline-flex" alignItems="center" gap={1}>
      <Icon as={LuTimer} boxSize={3} />
      <Text>{label}</Text>
    </Badge>
  )
}

function StatPill({
  label, value, color = 'purple', icon, active, onClick,
}: {
  label: string
  value: number
  color?: string
  icon?: any
  active?: boolean
  onClick?: () => void
}) {
  return (
    <Box
      as="button"
      onClick={onClick}
      textAlign="left"
      p={3}
      borderRadius="xl"
      border="1px solid"
      borderColor={active ? `${color}.400` : 'gray.200'}
      bg={active ? `${color}.50` : 'white'}
      transition="all 0.2s"
      _hover={{ borderColor: `${color}.300`, transform: 'translateY(-1px)' }}
    >
      <HStack spacing={2} mb={1}>
        {icon && <Icon as={icon} color={`${color}.500`} boxSize={3.5} />}
        <Text fontSize="xs" color="gray.600" fontWeight="600">{label}</Text>
      </HStack>
      <Text fontSize="xl" fontWeight="800" color={`${color}.700`}>{value}</Text>
    </Box>
  )
}
